import { VariableType } from 'json-to-graphql-query';

import { DocumentIndex } from '@/interfaces/documentIndex';
import { Procedure } from '@/interfaces/procedure';
import { EntityResource } from './entity.resource';

/**
 * Resource procedur
 *
 * @export
 * @class ProcedureResource
 * @extends {EntityResource<Procedure>}
 */
export class ProcedureResource extends EntityResource<Procedure> {
  public getFields = {
    id: true,
    name: true,
    templateId: true,
    statusId: true,
    data: true,
    description: true,
    interpretation: true,
    code: true,
    status: {
      id: true,
      name: true,
      color: true
    }
  };

  public listFields = {
    id: true,
    name: true,
    statusId: true,
    templateId: true,
    status: {
      id: true,
      name: true,
      color: true
    }
  };

  public listVariables = { filters: 'ProcedureFilters' };

  constructor() {
    super({
      entityName: 'procedure'
    });
  }

  /**
   * Pobranie identyfikatora procedury na podstawie wzoru procedury
   *
   * @param {number} templateId
   * @returns {Promise<number>}
   * @memberof ProcedureResource
   */
  public idByTemplateId(templateId: number): Promise<number> {
    const queryName = 'procedureIdByTemplate';
    const query = this.prepareOptions({
      query: {
        __variables: { templateId: 'Int!' },
        [queryName]: { __args: { templateId: new VariableType('templateId') } }
      }
    });
    return this.query({ query, variables: { templateId } }, queryName);
  }

  /**
   * Ustawia nowy status procedury
   *
   * @param {number} id
   * @param {number} statusId
   * @returns {Promise<number>}
   * @memberof ProcedureResource
   */
  public setStatus(id: number, statusId: number): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!', statusId: 'Int!' },
        procedureSetStatus: { __args: { id: new VariableType('id'), statusId: new VariableType('statusId') } }
      }
    });
    return this.mutation({ mutation, variables: { id, statusId } }, 'procedureSetStatus');
  }

  /**
   * Lista wypłat
   *
   * @param {*} variables
   * @returns {Promise<DocumentIndex[]>}
   * @memberof ProcedureResource
   */
  public paymentList(variables: any): Promise<DocumentIndex[]> {
    const query = this.prepareOptions({
      query: {
        __variables: {
          procedureId: 'Int!',
          date: 'DateScalar!',
          structureIds: '[Int!]',
          extraFilters: 'MongoFieldFilters'
        },
        procedurePaymentList: {
          __args: {
            date: new VariableType('date'),
            procedureId: new VariableType('procedureId'),
            structureIds: new VariableType('structureIds'),
            extraFilters: new VariableType('extraFilters')
          }
        }
      }
    });
    return this.query({ query, variables }, 'procedurePaymentList');
  }

  /**
   * Lista szkół dla których możliwe są wypłaty
   *
   * @param {*} variables
   * @returns {Promise<string[]>}
   * @memberof ProcedureResource
   */
  public paymentListSchools(variables: any): Promise<string[]> {
    const query = this.prepareOptions({
      query: {
        __variables: { procedureId: 'Int!', date: 'DateScalar!', structureIds: '[Int!]' },
        procedurePaymentListSchools: {
          __args: {
            date: new VariableType('date'),
            procedureId: new VariableType('procedureId'),
            structureIds: new VariableType('structureIds')
          }
        }
      }
    });
    return this.query({ query, variables }, 'procedurePaymentListSchools');
  }

  /**
   * Rozkład źródeł wypłat
   *
   * @param {*} variables
   * @returns {Promise<{ grant: number; contribution: number }>}
   * @memberof ProcedureResource
   */
  public paymentListSplit(variables: any): Promise<{ grant: number; contribution: number }> {
    const query = this.prepareOptions({
      query: {
        __variables: {
          procedureId: 'Int!',
          date: 'DateScalar!',
          documentIds: '[Int!]',
          structureIds: '[Int!]',
          extraFilters: 'MongoFieldFilters',
          paymentDate: 'DateScalar!',
          paymentType: 'PaymentType'
        },
        procedurePaymentListSplit: {
          __args: {
            date: new VariableType('date'),
            procedureId: new VariableType('procedureId'),
            documentIds: new VariableType('documentIds'),
            structureIds: new VariableType('structureIds'),
            extraFilters: new VariableType('extraFilters'),
            paymentDate: new VariableType('paymentDate'),
            paymentType: new VariableType('paymentType')
          },
          grant: true,
          contribution: true
        }
      }
    });
    return this.query({ query, variables }, 'procedurePaymentListSplit');
  }

  /**
   * Zapisuje listę wypłat jako załącznik
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ProcedureResource
   */
  public paymentListSubmitDraft(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          procedureId: 'Int!',
          date: 'DateScalar!',
          paymentDate: 'DateScalar!',
          documentIds: '[Int!]!',
          grant: 'Float',
          contribution: 'Float',
          extraFilters: 'MongoFieldFilters',
          packId: 'Int',
          overwriteData: '[DocumentInput!]',
          structureIds: '[Int!]',
          paymentType: 'PaymentType'
        },
        procedurePaymentListSubmitDraft: {
          __args: {
            date: new VariableType('date'),
            paymentDate: new VariableType('paymentDate'),
            documentIds: new VariableType('documentIds'),
            procedureId: new VariableType('procedureId'),
            grant: new VariableType('grant'),
            contribution: new VariableType('contribution'),
            extraFilters: new VariableType('extraFilters'),
            packId: new VariableType('packId'),
            overwriteData: new VariableType('overwriteData'),
            structureIds: new VariableType('structureIds'),
            paymentType: new VariableType('paymentType')
          }
        }
      }
    });

    return this.mutation({ mutation, variables }, 'procedurePaymentListSubmitDraft');
  }

  /**
   * Zapisuje listę wypłat jako załącznik
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ProcedureResource
   */
  public paymentListSendToMdok(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: {
          procedureId: 'Int!',
          date: 'DateScalar!',
          paymentDate: 'DateScalar!',
          documentIds: '[Int!]!',
          grant: 'Float',
          contribution: 'Float',
          extraFilters: 'MongoFieldFilters'
        },
        procedurePaymentListSendToMdok: {
          __args: {
            date: new VariableType('date'),
            paymentDate: new VariableType('paymentDate'),
            documentIds: new VariableType('documentIds'),
            procedureId: new VariableType('procedureId'),
            grant: new VariableType('grant'),
            contribution: new VariableType('contribution'),
            extraFilters: new VariableType('extraFilters')
          }
        }
      }
    });

    return this.mutation({ mutation, variables }, 'procedurePaymentListSendToMdok');
  }

  /**
   * Plan roczny
   *
   * @param {*} variables
   * @returns {Promise<string[][]>}
   * @memberof ProcedureResource
   */
  public plan(variables: any): Promise<string[][]> {
    const query = this.prepareOptions({
      query: {
        __variables: { procedureId: 'Int!', startDate: 'DateScalar!', endDate: 'DateScalar!' },
        procedurePlan: {
          __args: {
            procedureId: new VariableType('procedureId'),
            startDate: new VariableType('startDate'),
            endDate: new VariableType('endDate')
          }
        }
      }
    });

    return this.query({ query, variables }, 'procedurePlan');
  }

  /**
   * Tworzenie procedury na podstawie już istniejącej
   *
   * @param {*} variables
   * @returns {Promise<number>}
   * @memberof ProcedureResource
   */
  public copy(variables: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { procedureId: 'Int!', name: 'String!', schoolYear: 'String!' },
        procedureCopy: {
          __args: {
            procedureId: new VariableType('procedureId'),
            name: new VariableType('name'),
            schoolYear: new VariableType('schoolYear')
          }
        }
      }
    });
    return this.mutation({ mutation, variables }, 'procedureCopy');
  }
}

export const procedureResource = new ProcedureResource();
